import { useMutation, useQuery } from 'react-query';
import parse from 'url-parse';
import { URL_API } from '../constants';
import queryKeys from '../constants/queryKeys';
import { chatgenieAPI } from '../services/chatgenieAPI';
import { removeInvalidQueries } from '../utils';
import useWithDebouncedState from './useWithDebouncedState';

const updateActionConfiguration = data =>
  chatgenieAPI
    .instance({
      method: 'POST',
      url: `${URL_API}/api/inbox/actions/configuration`,
      data
    })
    .then(res => res.data.data);

const getConfigurations = queries =>
  chatgenieAPI
    .instance({
      method: 'GET',
      url: `/v1/configurations${parse.qs.stringify(removeInvalidQueries(queries), {
        addQueryPrefix: true
      })}`
    })
    .then(({ data }) => data || []);
const getConfigurationOptions = queries =>
  chatgenieAPI
    .instance({
      method: 'GET',
      url: `/v1/configurations/options${parse.qs.stringify(removeInvalidQueries(queries), {
        addQueryPrefix: true
      })}`
    })
    .then(({ data }) => data || []);
const getMappingConfigurations = queries =>
  chatgenieAPI
    .instance({
      method: 'GET',
      url: `/v1/contact-configuration-mappings${parse.qs.stringify(removeInvalidQueries(queries), {
        addQueryPrefix: true
      })}`
    })
    .then(({ data }) => data || []);
const searchConfigurations = queries =>
  chatgenieAPI
    .instance({
      method: 'GET',
      url: `/v1/configurations/search${parse.qs.stringify(removeInvalidQueries(queries), {
        addQueryPrefix: true
      })}`
    })
    .then(({ data }) => {
      const grouped = data.meta.properties.map(group => ({
        label: group.title,
        options: data.data?.[group.key]?.length
          ? data.data?.[group.key]?.map(option => ({
              ...option,
              parentKey: group.key,
              parentLabel: group.title
            }))
          : [
              {
                noOptions: true
              }
            ]
      }));
      const noValidOptions = grouped?.every(el => el.options?.[0]?.noOptions);
      return noValidOptions ? [] : grouped;
    });
const updateConfiguration = ({ id, data }) =>
  chatgenieAPI.instance({
    method: 'PUT',
    url: `/v1/configurations/${id}`,
    data
  });
const bulkMappingConfigurations = data =>
  chatgenieAPI.instance({
    method: 'PUT',
    url: `/v1/contact-configuration-mappings/bulk`,
    data
  });

const useConfigurations = ({
  defaultFilterQueries,
  configurationsQueryProps = { enabled: false },
  defaultMappingFilterQueries,
  mappingConfigurationsProps = { enabled: false },
  updateMutationProps,
  updateActionMutationProps,
  bulkMappingMutationProps
} = {}) => {
  const [filterQueries, setFilterQueries, debouncedFilterQueries] =
    useWithDebouncedState(defaultFilterQueries);
  const configurations = useQuery(
    [queryKeys.GET_CONFIGURATIONS, debouncedFilterQueries],
    () => getConfigurations(filterQueries),
    configurationsQueryProps
  );
  const mappingConfigurations = useQuery(
    [queryKeys.GET_CONFIGURATIONS, { mapping: true }],
    () => getMappingConfigurations(defaultMappingFilterQueries),
    mappingConfigurationsProps
  );
  const updateMutation = useMutation(updateConfiguration, updateMutationProps);
  const bulkMappingMutation = useMutation(bulkMappingConfigurations, bulkMappingMutationProps);

  const updateActionMutation = useMutation(updateActionConfiguration, updateActionMutationProps);

  return {
    configurations,
    mappingConfigurations,
    updateActionMutation,
    updateMutation,
    bulkMappingMutation,
    searchConfigurations,
    getConfigurations,
    getConfigurationOptions,
    setFilterQueries,
    filterQueries
  };
};

export default useConfigurations;
